<template>
	<TypographyText type="secondary">{{ $t('dashboard.signalStats.descriptions.' + selectedMetric) }}</TypographyText>
	<div v-if="hasStats" class="signals-stats-overview">
		<div class="signals-stats-grid">
			<div
				v-for="gnssItem in normalizedStats"
				:key="gnssItem.gnssCode"
				class="signals-stats-card"
			>
				<TypographyText strong>
					{{ $t('common.gnssLabels.' + gnssItem.gnssCode) }}
				</TypographyText>
				<div
					v-for="band in gnssItem.bands"
					:key="band.key"
					class="signals-stats-band"
				>
					<TypographyText type="secondary" class="signals-stats-band-label">
						{{ 'band ' + band.key }}
					</TypographyText>
					<div v-if="band.signals.length === 0" class="signals-stats-band-no-stats">
						{{ $t('settings.vrsSettingsForm.activeSignalsMessages.noSignals') }}
					</div>
					<div v-else class="signals-stats-band-chart">
						<div v-for="signal in band.signals" :key="signal.signal" class="signals-stats-bar">
							<div class="signals-stats-bar-count">
								{{ signal[selectedMetric] }}
							</div>
							<Popover placement="right" trigger="click" :getPopupContainer="getPopupContainer">
								<template #content>
									<VerticalLayout size="small">
										<TypographyText strong>{{ $t('dashboard.signalStats.labels.mountpoints') }}</TypographyText>
										<div class="mountpoints-list">
											<MountpointDisplayRich
												v-for="baseStation in signal.baseStations"
												:key="baseStation.id"
												:baseStation="baseStation"
											/>
										</div>
									</VerticalLayout>
								</template>
								<div
									class="signals-stats-bar-inner"
									:style="signal.barStyle"
								/>
							</Popover>
							<div class="signals-stats-bar-label">{{ signal.signal }}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { toRef, computed } from 'vue';
import { TypographyText, Popover } from 'ant-design-vue';

import Utils from '@/api/Utils';

import { useBaseStationsStore } from '@/stores/baseStations';
import { usePopupContainer } from '@/utils/usePopupContainer';

import MapHelpers from '@/components/map/utils/MapHelpers';

import MountpointDisplayRich from '@/components/baseStations/MountpointDisplayRich.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';


const BASE_SIGNAL_HEIGHT = 40;

export default {
	components: { TypographyText, Popover, MountpointDisplayRich, VerticalLayout },
	props: ['vrsSignalsStats', 'vrsSelectedSignals', 'selectedMetric'],
	setup(props) {
		const selectedMetricRef = toRef(props, 'selectedMetric');
		const vrsSignalsStatsRef = toRef(props, 'vrsSignalsStats');
		const vrsSelectedSignalsRef = toRef(props, 'vrsSelectedSignals');

		const hasStats = computed(() => (vrsSelectedSignalsRef.value || []).length > 0);

		const baseStationsStore = useBaseStationsStore();
		const normalizedStats = computed(() => {
			const stats = vrsSignalsStatsRef.value || [];
			const allValues = stats
				.flatMap(gnss => (gnss.bands || []))
				.flatMap(band => (band.signals || []))
				.map(signal => signal[selectedMetricRef.value]);
			const globalMaxValue = (allValues.length > 0 ? Math.max(...allValues) : 1) || 1;
			return (vrsSelectedSignalsRef.value || []).map(selectedGnss => {
				const gnssCode = selectedGnss.gnssCode;
				const color = '#' + MapHelpers.getGnssDataDisplay(gnssCode).color;
				const statsGnss = stats.find(item => item.gnssCode === gnssCode) || { bands: [] };
				const bands = selectedGnss.bands.map(selectedBand => {
					const statsBand = statsGnss.bands.find(band => band.key === selectedBand.key);
					const statsBandSignals = statsBand != null ? statsBand.signals : [];
					const displaySignals = statsBandSignals
						.map(signal => ({
							...signal,
							baseStations: baseStationsStore.items.filter(
								baseStation => signal.mountpoints.includes(baseStation.mntpnt_int)
							).sort(Utils.objectFieldMultiSorter('mntpnt_int')),
							barStyle: {
								height: (signal[selectedMetricRef.value] * BASE_SIGNAL_HEIGHT) / globalMaxValue + 'px',
								backgroundColor: color,
							},
						}))
						.sort((a, b) => b[selectedMetricRef.value] - a[selectedMetricRef.value]);
					return {
						key: selectedBand.key,
						signals: displaySignals,
					};
				});
				return {
					gnssCode,
					bands,
				};
			});
		});

		const { getPopupContainer } = usePopupContainer();

		return {
			vrsSignalsStatsRef,
			vrsSelectedSignalsRef,
			hasStats,
			normalizedStats,
			getPopupContainer,
		};
	},
}
</script>

<style scoped>
.signals-stats-overview {
	background-color: #fafafa;
	margin-top: 16px;
}

.signals-stats-controls {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 8px;
}

.signals-stats-grid {
	display: grid;
	overflow-x: auto;
	grid-auto-flow: column;
	grid-auto-columns: 1fr;
	gap: 6px;
}

.signals-stats-card {
	border-radius: 6px;
	padding: 6px 8px;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.signals-stats-band {
	margin-top: 2px;
}

.signals-stats-band-label {
	font-size: 11px;
}

.signals-stats-band-no-stats {
	font-size: 10px;
	opacity: 0.7;
}

.signals-stats-band-chart {
	margin-top: 2px;
	display: flex;
	align-items: flex-end;
	gap: 4px;
	min-height: 26px;
}

.signals-stats-bar {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-end;
	flex: 1 1 0;
	max-width: 20px;
}

.signals-stats-bar-count {
	font-size: 10px;
	line-height: 1.1;
	margin-bottom: 1px;
}

.signals-stats-bar-inner {
	width: 100%;
	border-radius: 3px 3px 0 0;
}

.signals-stats-bar-label {
	font-size: 10px;
	line-height: 1.1;
	margin-top: 1px;
	font-family: monospace;
	text-align: center;
	word-break: keep-all;
}

.mountpoints-list {
	max-height: 200px;
	overflow-x: hidden;
	overflow-y: auto;
}
</style>
